import { ArrowDownward, ArrowForward, ArrowUpward } from '@mui/icons-material';
import {
    Box,
    Collapse,
    Divider,
    Fab,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';

import { endOfDay, isBefore, isWithinInterval, startOfToday } from 'date-fns';
import getTime from 'date-fns/getTime';
import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import GuardedLink from '../../../../common/auth/GuardedLink';
import { EntityTypes, PricelistDto, ResourceType } from '../../../../common/PricePrgClient';
import { RootState } from '../../../../store';
import { toDateString } from '../../../../utils';
import { pricelistsSelected, showConnectPricelistDialog } from '../../../pricelist/redux/pricelistSlices';
import {
    getSelectedCustomer,
    getSelectedCustomerPricelistPeriods,
    getSelectedCustomerPricelists
} from '../../redux/customerSelectors';
import { fetchPricelistPeriodsForPricelists } from '../../redux/customerSlices';
import ConnectPricelistDialog from '../../../pricelist/ConnectPricelistDialog';

const mapStateToProps = (state: RootState) => {
    return {
        selectedCustomer: getSelectedCustomer(state),
        pricelists: getSelectedCustomerPricelists(state),
        pricelistPeriods: getSelectedCustomerPricelistPeriods(state),
        showConnectDialog: state.pricelist.ui.connectPricelistDialog.isVisible,
        appRestrictions: state.settings.applicationRestrictions
    };
};

type Props = ReturnType<typeof mapStateToProps>;

const PricelistCustomerTab = ({
    selectedCustomer,
    pricelists,
    pricelistPeriods,
    showConnectDialog,
    appRestrictions
}: Props) => {
    const dispatch = useDispatch();
    const [openPricelistIds, setOpenPricelistIds] = React.useState([] as number[]);
    const filteredPeriods = pricelistPeriods.filter(plp => !isBefore(new Date(plp.validTo), startOfToday()));

    useEffect(() => {
        if (pricelists != null) {
            const plIds = pricelists.map(pl => pl.pricelistId);
            if (plIds.length > 0) {
                dispatch(fetchPricelistPeriodsForPricelists.request(plIds));
            }
        }
    }, [dispatch, pricelists]);

    if (selectedCustomer == null) {
        return (
            <Typography variant="h3" sx={{ textAlign: 'center', fontWeight: 400, marginTop: '5%' }}>
                Choose a customer to view more details about it.
            </Typography>
        );
    }

    const handleOpenPricelist = (pl: PricelistDto) => {
        if (openPricelistIds.includes(pl.pricelistId)) {
            setOpenPricelistIds(plIds => plIds.filter(plId => plId !== pl.pricelistId));
        } else {
            setOpenPricelistIds(plIds => [...plIds, pl.pricelistId]);
        }
    };

    const isCurrent = (fromDate: string, toDate: string) => {
        return isWithinInterval(new Date(), { start: new Date(fromDate), end: endOfDay(new Date(toDate)) });
    };

    const dispatchGotoSelectedPricelist = (pricelistId: number) => {
        dispatch(pricelistsSelected([pricelistId]));
    };

    const PricelistDivider = () => (
        <Grid container direction="row">
            <Grid item xs={12} md={12} lg={6}>
                <Divider sx={{ margin: '16px 0' }} orientation="horizontal" variant="fullWidth" />
            </Grid>
        </Grid>
    );
    const handleConnectPricelist = () => {
        dispatch(showConnectPricelistDialog({ pricelistId: null, customerId: selectedCustomer.customerId }));
    };
    return (
        <>
            <Box sx={{ display: 'inline-flex' }}>
                <Typography variant="h6">Pricelists linked to customer: {selectedCustomer.name}</Typography>
                <Tooltip title="Connect pricelist">
                    <Fab
                        color="primary"
                        disabled={appRestrictions[EntityTypes.PricelistConnection].isCreateDisabled}
                        sx={{ ml: 2 }}
                        size="small"
                        onClick={handleConnectPricelist}
                    >
                        <LinkIcon />
                    </Fab>
                </Tooltip>
            </Box>
            <PricelistDivider />
            <Grid container direction="column" spacing={2}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '10rem' }}>Name</TableCell>
                            <TableCell sx={{ width: '8rem' }}>Pricelist ref.</TableCell>
                            <TableCell sx={{ width: '6rem' }}>Base pricelist</TableCell>
                            <TableCell sx={{ width: '6rem' }}>Child pricelist</TableCell>
                            <TableCell sx={{ width: '4rem' }}>Periods</TableCell>
                            <TableCell sx={{ width: '14rem' }}>Valid from/to</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pricelists.map(pl => (
                            <TableRow key={pl.pricelistId}>
                                <TableCell sx={{ width: '10rem' }}>{pl.name}</TableCell>
                                <TableCell sx={{ width: '8rem' }}>{pl.pricelistRef}</TableCell>
                                <TableCell sx={{ width: '6rem' }}>
                                    {pl.isBasePricelist ? 'Yes' : 'No'}
                                </TableCell>
                                <TableCell sx={{ width: '6rem' }}>
                                    {pl.parentPricelistId != null ? 'Yes' : 'No'}
                                </TableCell>
                                <TableCell sx={{ width: '4rem' }}>
                                    <IconButton
                                        aria-label="expand row"
                                        size="small"
                                        onClick={() => handleOpenPricelist(pl)}
                                    >
                                        {openPricelistIds.includes(pl.pricelistId) ? (
                                            <Tooltip
                                                sx={{ display: 'inline-flex' }}
                                                title="Click to hide periods"
                                            >
                                                <ArrowUpward />
                                            </Tooltip>
                                        ) : (
                                            <Tooltip
                                                sx={{ display: 'inline-flex' }}
                                                title="Click to see periods"
                                            >
                                                <ArrowDownward />
                                            </Tooltip>
                                        )}
                                    </IconButton>
                                    <GuardedLink
                                        resource={ResourceType.Pricelist}
                                        link={'/pricelists'}
                                        childComponents={
                                            <Tooltip sx={{ display: 'inline-flex' }} title="Go to pricelist">
                                                <IconButton
                                                    onClick={() =>
                                                        dispatchGotoSelectedPricelist(pl.pricelistId!)
                                                    }
                                                    size="small"
                                                >
                                                    <ArrowForward />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    />
                                </TableCell>
                                <TableCell>
                                    <Collapse
                                        in={openPricelistIds.includes(pl.pricelistId)}
                                        timeout="auto"
                                        unmountOnExit
                                    >
                                        <Table size="small">
                                            <TableBody>
                                                {filteredPeriods
                                                    .filter(fplp => fplp.pricelistId === pl.pricelistId)
                                                    .sort(
                                                        (p1, p2) =>
                                                            getTime(new Date(p1.validFrom)) -
                                                            getTime(new Date(p2.validFrom))
                                                    )
                                                    .map(plp => (
                                                        <TableRow
                                                            key={plp.pricelistPeriodId}
                                                            sx={{ width: '14rem' }}
                                                        >
                                                            <TableCell
                                                                sx={
                                                                    isCurrent(plp.validFrom, plp.validTo)
                                                                        ? { fontWeight: 800 }
                                                                        : undefined
                                                                }
                                                            >
                                                                {toDateString(plp?.validFrom)}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={
                                                                    isCurrent(plp.validFrom, plp.validTo)
                                                                        ? { fontWeight: 800 }
                                                                        : undefined
                                                                }
                                                            >
                                                                {toDateString(plp?.validTo)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                            </TableBody>
                                        </Table>
                                    </Collapse>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Grid>
            {showConnectDialog && (
                <ConnectPricelistDialog customerId={selectedCustomer.customerId} pricelistId={undefined} />
            )}
        </>
    );
};

export default connect(mapStateToProps)(PricelistCustomerTab);
